// ============================================================================
// OPPORTUNITY PIPELINE TAB COMPONENT
// Sales opportunity tracking with stages, probability, and revenue
// Date: January 20, 2025
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Target, TrendingUp, DollarSign, Calendar, User, Trash2, Edit2, CheckCircle } from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { useModuleColor } from '@/hooks/useModuleColor';
import { confirm } from '@/lib/notifications';

// ============================================================================
// TYPES
// ============================================================================

interface PipelineStage {
  id: string;
  stage: 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost';
  probability: number;
  expected_revenue: number;
  expected_close_date?: string;
  notes?: string;
  recorded_at: string;
  recorded_by?: string;
}

interface OpportunityPipelineTabProps {
  workItemId: string;
  isActive: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR'
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getStageLabel = (stage: string): string => {
  const labels: Record<string, string> = {
    'lead': 'Lead',
    'qualified': 'Qualified',
    'proposal': 'Proposal Sent',
    'negotiation': 'Negotiation',
    'closed_won': 'Closed Won',
    'closed_lost': 'Closed Lost'
  };
  return labels[stage] || stage;
};

const getStageColor = (stage: string): string => {
  const colors: Record<string, string> = {
    'lead': '#6B7280',
    'qualified': '#3B82F6',
    'proposal': '#8B5CF6',
    'negotiation': '#F59E0B',
    'closed_won': '#10B981',
    'closed_lost': '#EF4444'
  };
  return colors[stage] || '#6B7280';
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function OpportunityPipelineTab({ workItemId, isActive }: OpportunityPipelineTabProps) {
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newStage, setNewStage] = useState({
    stage: 'lead' as PipelineStage['stage'],
    probability: 10,
    expected_revenue: '',
    expected_close_date: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const hasLoadedRef = useRef(false);
  const { currentTheme } = useTheme();
  const { moduleColor } = useModuleColor('total-crm', currentTheme.colors.primary || '#0d9488');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/pipeline?limit=50`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStages(result.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading pipeline data:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    hasLoadedRef.current = false;
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadData();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadData]);

  const handleAddStage = async () => {
    if (!newStage.expected_revenue) return;

    try {
      setSubmitting(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/pipeline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          stage: newStage.stage,
          probability: newStage.probability,
          expected_revenue: parseFloat(newStage.expected_revenue),
          expected_close_date: newStage.expected_close_date || null,
          notes: newStage.notes || null,
          recorded_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setStages([result.data, ...stages]);
          setNewStage({
            stage: 'lead',
            probability: 10,
            expected_revenue: '',
            expected_close_date: '',
            notes: ''
          });
        }
      }
    } catch (error) {
      console.error('Error adding stage:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Delete this pipeline stage record?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/pipeline?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        setStages(stages.filter(s => s.id !== id));
      }
    } catch (error) {
      console.error('Error deleting stage:', error);
    }
  };

  const isDark = currentTheme.mode === 'dark';

  // Calculate weighted revenue
  const currentStage = stages[0];
  const weightedRevenue = currentStage 
    ? (currentStage.expected_revenue * currentStage.probability) / 100 
    : 0;

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Status Summary */}
      {currentStage && (
        <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Current Stage
              </div>
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getStageColor(currentStage.stage) }}
                />
                <span className={`text-lg font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  {getStageLabel(currentStage.stage)}
                </span>
              </div>
            </div>
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Probability
              </div>
              <div className={`text-lg font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {currentStage.probability}%
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="h-2 rounded-full transition-all"
                  style={{ 
                    width: `${currentStage.probability}%`,
                    backgroundColor: getStageColor(currentStage.stage)
                  }}
                />
              </div>
            </div>
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Expected Revenue
              </div>
              <div className={`text-lg font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {formatCurrency(currentStage.expected_revenue)}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                Weighted: {formatCurrency(weightedRevenue)}
              </div>
            </div>
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Expected Close
              </div>
              <div className={`text-lg font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {currentStage.expected_close_date 
                  ? formatDate(currentStage.expected_close_date)
                  : 'Not set'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Stage */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <h4 className={`font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
          Update Pipeline Stage
        </h4>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Sales Stage
              </label>
              <select
                value={newStage.stage}
                onChange={(e) => {
                  const stage = e.target.value as PipelineStage['stage'];
                  // Auto-set probability based on stage
                  const probabilities: Record<string, number> = {
                    'lead': 10,
                    'qualified': 25,
                    'proposal': 50,
                    'negotiation': 75,
                    'closed_won': 100,
                    'closed_lost': 0
                  };
                  setNewStage({ 
                    ...newStage, 
                    stage,
                    probability: probabilities[stage] || 10
                  });
                }}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="lead">Lead</option>
                <option value="qualified">Qualified</option>
                <option value="proposal">Proposal Sent</option>
                <option value="negotiation">Negotiation</option>
                <option value="closed_won">Closed Won</option>
                <option value="closed_lost">Closed Lost</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Probability (%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={newStage.probability}
                onChange={(e) => setNewStage({ ...newStage, probability: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className={`text-center text-sm font-medium mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {newStage.probability}%
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Expected Revenue (ZAR)
              </label>
              <input
                type="number"
                step="0.01"
                value={newStage.expected_revenue}
                onChange={(e) => setNewStage({ ...newStage, expected_revenue: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Expected Close Date
              </label>
              <input
                type="date"
                value={newStage.expected_close_date}
                onChange={(e) => setNewStage({ ...newStage, expected_close_date: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Notes
            </label>
            <textarea
              value={newStage.notes}
              onChange={(e) => setNewStage({ ...newStage, notes: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              rows={2}
              placeholder="Add notes about this stage update..."
            />
          </div>
          <button
            onClick={handleAddStage}
            disabled={submitting || !newStage.expected_revenue}
            className="w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 hover:opacity-90 transition-colors"
            style={{ backgroundColor: moduleColor }}
          >
            {submitting ? 'Adding...' : 'Add Pipeline Update'}
          </button>
        </div>
      </div>

      {/* Stage History */}
      {stages.length === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No pipeline history recorded yet
        </div>
      ) : (
        <div className="space-y-3">
          <h4 className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
            Pipeline History
          </h4>
          {stages.map((stage, index) => (
            <div
              key={stage.id}
              className={`p-4 rounded-lg border-l-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              style={{ borderLeftColor: getStageColor(stage.stage) }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: getStageColor(stage.stage) }}
                    >
                      {getStageLabel(stage.stage)}
                    </span>
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {stage.probability}% probability
                    </span>
                    {index === 0 && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Expected Revenue:
                      </span>
                      <span className={`ml-2 font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {formatCurrency(stage.expected_revenue)}
                      </span>
                    </div>
                    {stage.expected_close_date && (
                      <div>
                        <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Expected Close:
                        </span>
                        <span className={`ml-2 font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {formatDate(stage.expected_close_date)}
                        </span>
                      </div>
                    )}
                  </div>
                  {stage.notes && (
                    <div className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {stage.notes}
                    </div>
                  )}
                  <div className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {formatTimeAgo(stage.recorded_at)}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(stage.id)}
                  className="text-red-600 hover:text-red-800 ml-4"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
